import React, { type FunctionComponent, useEffect, useMemo } from 'react'
import styled from '@emotion/styled'
import { Button, spacing, LoadingDots } from '@retailer-platform/shared-components'
import { useDomainMessages } from 'domains/aggregate-store-performance/utils/domain/intl'
import { FullScreenModal } from 'domains/aggregate-store-performance/components/full-screen-modal/FullScreenModal'
import { StoreGroupsForm } from 'legacy/sections/store-groups/components/form/StoreGroupsForm'
import { useCreateStoreGroupOnSubmit } from 'legacy/sections/store-groups/components/add/utils/submitForm'
import { useUpdateStoreGroupOnSubmit } from 'legacy/sections/store-groups/components/edit/utils/submitForm'
import { useFetchStoreGroup } from 'legacy/sections/store-groups/graphql/fetch.hooks'

const formId = 'store-group-form-modal'

const ActionButton = styled(Button)({
  height: spacing.X56,
  width: '190px',
  margin: `${spacing.X12} ${spacing.X16}`,
})

interface StoreGroupFormModalProps {
  isOpen: boolean
  onClose: () => void
  storeGroupId?: string
}

export const StoreGroupFormModal: FunctionComponent<StoreGroupFormModalProps> = ({
  isOpen,
  onClose,
  storeGroupId,
}) => {
  const messages = useDomainMessages({
    modalTitle: 'aggregateStorePerformanceDomain.storeGroupFormModal.title',
    cancel: 'aggregateStorePerformanceDomain.storeGroupFormModal.cancel',
    create: 'aggregateStorePerformanceDomain.storeGroupFormModal.create',
    save: 'aggregateStorePerformanceDomain.storeGroupFormModal.save',
  })

  const {
    handleSubmit: handleSubmitCreate,
    createdStoreGroup,
    loading: loadingCreate,
  } = useCreateStoreGroupOnSubmit()
  const { storeGroup, loading: loadingStoreGroup } = useFetchStoreGroup({
    storeGroupId: storeGroupId,
  })
  const {
    handleSubmit: handleSubmitUpdate,
    updatedStoreGroup,
    loading: loadingUpdate,
  } = useUpdateStoreGroupOnSubmit()

  const handleSubmit = storeGroupId ? handleSubmitUpdate : handleSubmitCreate
  const modifiedStoreGroup = storeGroupId ? updatedStoreGroup : createdStoreGroup
  const loading = loadingCreate || loadingStoreGroup || loadingUpdate

  useEffect(() => {
    if (modifiedStoreGroup) {
      onClose()
    }
  }, [modifiedStoreGroup, onClose])

  const body = useMemo(
    () => (
      <>
        {loadingStoreGroup ? (
          <LoadingDots centered />
        ) : (
          <StoreGroupsForm
            formId={formId}
            handleSubmit={handleSubmit}
            showFormConfirmBar={false}
            existingStoreGroup={storeGroup}
          />
        )}
      </>
    ),
    [loadingStoreGroup, handleSubmit, storeGroup]
  )

  const footer = useMemo(
    () => (
      <>
        <ActionButton variant="secondary" onClick={onClose}>
          {messages.cancel}
        </ActionButton>
        <ActionButton variant="secondary" type="submit" form={formId} disabled={loading}>
          {storeGroupId ? messages.save : messages.create}
        </ActionButton>
      </>
    ),
    [messages, storeGroupId, loading, onClose]
  )

  return (
    <FullScreenModal
      onClose={onClose}
      isOpen={isOpen}
      title={messages.modalTitle}
      body={body}
      footer={footer}
    />
  )
}
